import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Typography,
  Badge,
} from "@altimateai/lego";
import { RulesDetailedBreakdown } from "./RulesDetailedBreakdown";
import type { UserGroupRulesStats } from "../../services/rbac";

interface OverviewTabProps {
  rulesData: UserGroupRulesStats;
}

export const OverviewTab = ({ rulesData }: OverviewTabProps) => {
  return (
    <div className="al-space-y-6 al-mt-6">
      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
          <CardDescription>Summary of user group access rules</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-grid al-grid-cols-4 al-gap-4">
            <div className="al-p-4 al-bg-blue-50 al-border al-border-blue-200 al-rounded-lg">
              <Typography className="al-text-blue-700 al-uppercase al-mb-1">Total Rules</Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_rules}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-green-50 al-border al-border-green-200 al-rounded-lg">
              <Typography className="al-text-green-700 al-uppercase al-mb-1">
                User Groups
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_user_groups}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-purple-50 al-border al-border-purple-200 al-rounded-lg">
              <Typography className="al-text-purple-700 al-uppercase al-mb-1">
                Entity Types
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_entity_types}
              </Typography>
            </div>
            <div className="al-p-4 al-bg-orange-50 al-border al-border-orange-200 al-rounded-lg">
              <Typography className="al-text-orange-700 al-uppercase al-mb-1">
                Match Types
              </Typography>
              <Typography variant="h3" weight="semibold">
                {rulesData.totals.total_match_types}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>

      {rulesData.per_tenant && rulesData.per_tenant.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tenants with Rules</CardTitle>
            <CardDescription>Distribution of rules across tenants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden">
              <table className="al-w-full al-border-collapse">
                <thead className="al-bg-gray-50">
                  <tr>
                    <th className="al-px-4 al-py-3 al-text-left">
                      <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                        Tenant
                      </Typography>
                    </th>
                    <th className="al-px-4 al-py-3 al-text-left">
                      <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                        Total Rules
                      </Typography>
                    </th>
                    <th className="al-px-4 al-py-3 al-text-left">
                      <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                        User Groups
                      </Typography>
                    </th>
                    <th className="al-px-4 al-py-3 al-text-left">
                      <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                        Entity Types
                      </Typography>
                    </th>
                  </tr>
                </thead>
                <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                  {rulesData.per_tenant
                    .filter(tenant => tenant.totals.total_rules > 0)
                    .sort((a, b) => b.totals.total_rules - a.totals.total_rules)
                    .map((tenant, index) => (
                      <tr key={index} className="al-hover:bg-gray-50">
                        <td className="al-px-4 al-py-3">
                          <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
                            {tenant.tenant.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="al-px-4 al-py-3">
                          <Typography variant="section-heading">
                            {tenant.totals.total_rules}
                          </Typography>
                        </td>
                        <td className="al-px-4 al-py-3">
                          <Typography className="al-text-gray-600">
                            {tenant.totals.total_user_groups}
                          </Typography>
                        </td>
                        <td className="al-px-4 al-py-3">
                          <Typography className="al-text-gray-600">
                            {tenant.totals.total_entity_types}
                          </Typography>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Rules by Entity Type</CardTitle>
          <CardDescription>What types of Snowflake entities are being filtered</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-border al-border-gray-200 al-rounded-lg al-overflow-hidden">
            <table className="al-w-full al-border-collapse">
              <thead className="al-bg-gray-50">
                <tr>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Entity Type
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      Total Rules
                    </Typography>
                  </th>
                  <th className="al-px-4 al-py-3 al-text-left">
                    <Typography className="al-text-gray-700 al-uppercase al-font-medium">
                      User Groups
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="al-bg-white">
                {rulesData.by_entity.map((stat, index) => (
                  <tr key={index} className="al-border-b al-border-gray-200">
                    <td className="al-px-4 al-py-4">
                      <Badge className="!al-bg-indigo-100 !al-text-indigo-700 al-border al-border-indigo-200">
                        {stat.entity_type}
                      </Badge>
                    </td>
                    <td className="al-px-4 al-py-4">
                      <Typography variant="section-heading">{stat.total_rules}</Typography>
                    </td>
                    <td className="al-px-4 al-py-4">
                      <Typography className="al-text-gray-600">{stat.user_groups}</Typography>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Rules by Match Type</CardTitle>
          <CardDescription>How filtering patterns are applied</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="al-grid al-grid-cols-3 al-gap-4">
            {rulesData.by_match_type.map((stat, index) => (
              <div
                key={index}
                className="al-p-4 al-bg-gray-50 al-border al-border-gray-200 al-rounded-lg"
              >
                <Badge className="!al-bg-teal-100 !al-text-teal-700 al-border al-border-teal-200 al-mb-2">
                  {stat.match_type}
                </Badge>
                <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                  {stat.rule_count}
                </Typography>
                <Typography className="al-text-gray-500">
                  {((stat.rule_count / rulesData.totals.total_rules) * 100).toFixed(1)}% of total
                </Typography>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <RulesDetailedBreakdown byEntityAndMatch={rulesData.by_entity_and_match} />
    </div>
  );
};
